/**
 * Bestelpool Lijst Pagina
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { bestellingenService, BestellingListItem, BestellingStatus } from '../../services/bestellingen.service';

const BestellingenList = () => {
  const navigate = useNavigate();
  const [bestellingen, setBestellingen] = useState<BestellingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bestelpool toont alleen concept items
  const [statusFilter] = useState<BestellingStatus | ''>('concept');

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBestelling, setSelectedBestelling] = useState<BestellingListItem | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState<{ title: string; message: string; type: 'success' | 'error' }>({ title: '', message: '', type: 'success' });

  // Referentie modal state
  const [showReferentieModal, setShowReferentieModal] = useState(false);
  const [referentie, setReferentie] = useState('');

  // Checkbox selectie state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const initialLoadDone = useRef(false);

  const loadBestellingen = useCallback(async () => {
    try {
      setLoading(true);
      const data = await bestellingenService.getAll(statusFilter || undefined);
      setBestellingen(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van de bestelpool');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadBestellingen();
  }, [loadBestellingen]);

  // Auto-select alle concept items alleen bij eerste load
  useEffect(() => {
    if (!initialLoadDone.current && bestellingen.length > 0) {
      const conceptIds = bestellingen
        .filter(b => b.status === 'concept')
        .map(b => b.id);
      setSelectedIds(new Set(conceptIds));
      initialLoadDone.current = true;
    }
  }, [bestellingen]);

  // Toggle selectie
  const toggleSelection = (id: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Open referentie modal voor bestellen
  const handleBestelSelectie = () => {
    const selectedIds_array = [...selectedIds].filter(id =>
      bestellingen.find(b => b.id === id && b.status === 'concept')
    );

    if (selectedIds_array.length === 0) {
      alert('Geen items geselecteerd om te bestellen');
      return;
    }

    // Toon referentie modal
    setReferentie('');
    setShowReferentieModal(true);
  };

  // Bevestig bestelling met referentie
  const confirmBestelling = async () => {
    if (!referentie.trim()) {
      alert('Vul een referentie in');
      return;
    }

    const selectedIds_array = [...selectedIds].filter(id =>
      bestellingen.find(b => b.id === id && b.status === 'concept')
    );

    setShowReferentieModal(false);

    try {
      const result = await bestellingenService.bulkBestellen(selectedIds_array, referentie.trim());

      await loadBestellingen();

      setMessageModalContent({
        title: 'Bestellingen Verstuurd',
        message: result.message,
        type: 'success'
      });
      setShowMessageModal(true);
    } catch (err: any) {
      setMessageModalContent({
        title: 'Fout',
        message: err.message || 'Fout bij het bestellen',
        type: 'error'
      });
      setShowMessageModal(true);
    }
  };


  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleDelete = (bestelling: BestellingListItem) => {
    setSelectedBestelling(bestelling);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedBestelling) return;
    try {
      await bestellingenService.delete(selectedBestelling.id);
      await loadBestellingen();
      setShowDeleteModal(false);
      setSelectedBestelling(null);
    } catch (err: any) {
      alert(err.message || 'Fout bij het verwijderen van de bestelling');
    }
  };

  const handleMarkeerOntvangen = async (id: number) => {
    try {
      await bestellingenService.markeerAlsOntvangen(id);
      await loadBestellingen();
    } catch (err: any) {
      alert(err.message || 'Fout bij het markeren als ontvangen');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Bestelpool laden...</div>
      </div>
    );
  }

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    padding: '0.75rem 1rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#495057'
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Bestelpool</h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}


      {/* Header */}
      {bestellingen.length > 0 && (
        <div style={{
          ...headerStyle,
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0'
        }}>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Leverancier</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Artikelnr.</div>
          <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Omschrijving</div>
          <div style={{ flex: '0.5', minWidth: 0, paddingRight: '1rem', textAlign: 'center' }}>Aantal</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Prijs</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Totaal</div>
          <div style={{ flex: '1.2', minWidth: 0 }}>Acties</div>
        </div>
      )}


      {/* List */}
      {bestellingen.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {bestellingen.map((bestelling) => (
            <div key={bestelling.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #dee2e6'
            }}>
              <div style={{ flex: '1', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bestelling.leverancier_naam || '-'}
              </div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bestelling.artikelnummer || '-'}
              </div>
              <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bestelling.omschrijving || '-'}
              </div>
              <div style={{ flex: '0.5', minWidth: 0, paddingRight: '1rem', textAlign: 'center' }}>
                {bestelling.aantal || '-'}
              </div>
              <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bestelling.stukprijs ? formatCurrency(bestelling.stukprijs) : '-'}
              </div>
              <div style={{ flex: '0.6', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatCurrency(bestelling.totaal_incl_btw)}
              </div>
              <div style={{ flex: '1.2', minWidth: 0, display: 'flex', gap: '0.25rem', flexWrap: 'nowrap', alignItems: 'center' }}>
                {bestelling.status === 'concept' && (
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginRight: '0.5rem'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(bestelling.id)}
                      onChange={() => toggleSelection(bestelling.id)}
                      style={{
                        width: '20px',
                        height: '20px',
                        accentColor: '#10b981',
                        cursor: 'pointer'
                      }}
                    />
                  </label>
                )}
                {bestelling.status === 'besteld' && (
                  <button
                    className="btn btn-sm"
                    onClick={() => handleMarkeerOntvangen(bestelling.id)}
                    style={{
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      backgroundColor: '#e5e7eb',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem'
                    }}
                  >
                    Ontvangen
                  </button>
                )}
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => navigate(`/bestelpool/${bestelling.id}`)}
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Bekijken
                </button>
                {(bestelling.status === 'concept' || bestelling.status === 'geannuleerd') && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(bestelling)}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    Verwijderen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {bestellingen.length === 0 && !loading && (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen items in bestelpool gevonden
        </div>
      )}

      {bestellingen.length > 0 && (
        <div className="table-footer" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0 }}>{bestellingen.length} bestelling(en) gevonden</p>
          {bestellingen.some(b => b.status === 'concept') && (
            <button
              className="btn btn-primary"
              onClick={handleBestelSelectie}
              disabled={![...selectedIds].some(id => bestellingen.find(b => b.id === id && b.status === 'concept'))}
              style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }}
            >
              Bestel selectie ({[...selectedIds].filter(id => bestellingen.find(b => b.id === id && b.status === 'concept')).length})
            </button>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedBestelling && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc2626' }}>Bestelling Verwijderen</h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Weet je zeker dat je deze bestelling wilt verwijderen?
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowDeleteModal(false); setSelectedBestelling(null); }}
              >
                Annuleren
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{
              marginTop: 0,
              marginBottom: '1rem',
              color: messageModalContent.type === 'success' ? '#059669' : '#dc2626'
            }}>
              {messageModalContent.title}
            </h3>
            <p style={{ marginBottom: '1.5rem', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
              {messageModalContent.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className={`btn ${messageModalContent.type === 'success' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setShowMessageModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referentie Modal */}
      {showReferentieModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1f2937' }}>
              Bestelling Versturen
            </h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                Referentie *
              </label>
              <input
                type="text"
                value={referentie}
                onChange={(e) => setReferentie(e.target.value)}
                className="form-control"
                placeholder="Bijv. projectnaam of ordernummer..."
                style={{ width: '100%' }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowReferentieModal(false)}
              >
                Annuleren
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmBestelling}
                disabled={!referentie.trim()}
              >
                Versturen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BestellingenList;
